import { useMemo } from "preact/hooks";
import type { MapGeometry } from "../shared/types";
import {
  roundedMapRouteSegments,
  type VenueMapRoutePoint,
  type VenueMapRouteSegment,
} from "./venueMapRouting";

export interface VenueMapRouteData {
  points: readonly VenueMapRoutePoint[];
  /** Safe bends prepared by the routing worker; older direct consumers may omit them. */
  segments?: readonly VenueMapRouteSegment[];
  fromStopId: string;
  toStopId: string;
  /** Route obstacles may include booths omitted from the visible geometry. */
  geometry?: MapGeometry;
}

/** The same floor-plane route is projected by each map view. */
export function VenueMapRoute({
  route,
  geometry,
  project,
}: {
  route: VenueMapRouteData;
  geometry: MapGeometry;
  project: (point: VenueMapRoutePoint) => { x: number; y: number };
}) {
  const rounded = useMemo(
    () =>
      route.segments ??
      roundedMapRouteSegments(route.points, route.geometry || geometry),
    [route.points, route.segments, route.geometry, geometry],
  );
  const segments = rounded.map((segment, index) => {
    const start = project(segment.start);
    const end = project(segment.end);
    return {
      start,
      end,
      control: segment.control && project(segment.control),
      index,
      length: Math.hypot(end.x - start.x, end.y - start.y),
    };
  });
  if (!segments.length) return null;
  const path = [
    `M${segments[0].start.x},${segments[0].start.y}`,
    ...segments.map(({ end, control }) =>
      control
        ? `Q${control.x},${control.y} ${end.x},${end.y}`
        : `L${end.x},${end.y}`,
    ),
  ].join(" ");
  // Put a direction arrow in an aisle, away from the endpoint stand interiors.
  const interior = segments.filter(
    (segment) =>
      !segment.control &&
      segment.index > 0 &&
      segment.index < segments.length - 1 &&
      segment.length > 14,
  );
  const arrow = (interior.length ? interior : segments)
    .filter((segment) => !segment.control && segment.length > 14)
    .sort((a, b) => b.length - a.length)[0];
  const arrowPath =
    arrow &&
    (() => {
      const dx = (arrow.end.x - arrow.start.x) / arrow.length;
      const dy = (arrow.end.y - arrow.start.y) / arrow.length;
      const x = (arrow.start.x + arrow.end.x) / 2;
      const y = (arrow.start.y + arrow.end.y) / 2;
      return `M${x + dx * 7},${y + dy * 7} L${x - dx * 5 - dy * 5},${y - dy * 5 + dx * 5} L${x - dx * 5 + dy * 5},${y - dy * 5 - dx * 5} Z`;
    })();
  return (
    <g
      key={`${route.fromStopId}:${route.toStopId}`}
      className="venue-map-route"
      aria-hidden="true"
      pointerEvents="none"
    >
      <path className="venue-map-wayfinding-halo" d={path} />
      <path
        className="venue-map-wayfinding"
        data-from-stop={route.fromStopId}
        data-to-stop={route.toStopId}
        d={path}
      />
      <path className="venue-map-wayfinding-highlight" d={path} />
      {arrowPath && (
        <path className="venue-map-wayfinding-arrow" d={arrowPath} />
      )}
    </g>
  );
}
